// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { FallbackRender } from "@sentry/react";
import dedent from "dedent";
import React from "react";

import { CopyWrapper, PageContainer } from "~@jii/common-ui";
import { useCommonTranslations } from "~@jii/translation";

type ErrorPageProps = {
  error: Error;
};

const ErrorPageMainContentWrapped = ({ error }: ErrorPageProps) => {
  const { t } = useCommonTranslations();

  const contents = dedent`# ${t(($) => $.errorPage.heading)}

  ${t(($) => $.errorPage.message)}`;

  return (
    <PageContainer>
      <CopyWrapper>{contents}</CopyWrapper>
    </PageContainer>
  );
};

type FallbackProps = Partial<Parameters<FallbackRender>[0]>;

/**
 * A Sentry ErrorBoundary fallback function will be called within the body of a class
 * component, which means it can't contain any React hook calls. This extra wrapper
 * prevents the provided component from breaking in that context and should have no
 * effect otherwise.
 */
export function asErrorBoundaryFallback<ComponentProps extends FallbackProps>(
  Component: React.FC<ComponentProps>,
) {
  const Fallback = (props: ComponentProps) => <Component {...props} />;
  return Fallback;
}

export const ErrorPageMainContent = asErrorBoundaryFallback(
  ErrorPageMainContentWrapped,
);
